// Breadcrumb — the location trail on the React Aria foundation (ADR-029), per
// COMPONENTS §Catalog. Links route through the React Aria RouterProvider
// (wired in RootLayout) — real links, modifier-click works. The CURRENT page
// is plain text (aria-current="page", never a link); separators are decorative
// carets that flip under RTL (the directional-icon convention).
//
// Production variations built in: optional per-item icon (the home glyph),
// long entity names truncate (max-w + truncate + title). Auto-collapse of
// deep trails ("…") is deferred until a real surface produces one — additive.

import type { ReactNode } from 'react';
import { Breadcrumbs as AriaBreadcrumbs, Breadcrumb as AriaBreadcrumb, Link as AriaLink } from 'react-aria-components';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';

export interface BreadcrumbItem {
  label: string;
  /** Omit on the last (current-page) item. */
  href?: string;
  /** Client-side activation instead of a routed href (e.g. a peek swap via openPeek —
   *  replace-never-stack). Renders a link-role control; takes precedence over `href`. */
  onAction?: () => void;
  /** Leading glyph (the home icon on the first item). Decorative. */
  icon?: ReactNode;
}

export interface BreadcrumbProps {
  /** Trail in order, root first; the last item is the current page. */
  items: BreadcrumbItem[];
  className?: string;
}

const itemText = 'max-w-[14rem] truncate text-sm';

export function Breadcrumb({ items, className = '' }: BreadcrumbProps) {
  const { t } = useTranslation('common');

  return (
    <nav aria-label={t('breadcrumb.label')} className={className}>
      <AriaBreadcrumbs className="flex items-center gap-1.5 flex-wrap">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <AriaBreadcrumb key={i} id={i} className="flex items-center gap-1.5 min-w-0">
              {isLast || (!item.href && !item.onAction) ? (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  title={item.label}
                  className={`${itemText} flex items-center gap-1 text-[var(--color-text-primary)] font-medium`}
                >
                  {item.icon && (
                    <span aria-hidden className="shrink-0 text-[var(--color-text-secondary)]">
                      {item.icon}
                    </span>
                  )}
                  {item.label}
                </span>
              ) : (
                <AriaLink
                  {...(item.onAction ? { onPress: item.onAction } : { href: item.href })}
                  className={
                    `${itemText} flex items-center gap-1 text-[var(--color-text-secondary)] ` +
                    'data-[hovered]:text-[var(--color-text-primary)] data-[hovered]:underline data-[pressed]:opacity-80 rounded-sm outline-none ' +
                    'focus-ring'
                  }
                >
                  {item.icon && (
                    <span aria-hidden className="shrink-0">
                      {item.icon}
                    </span>
                  )}
                  <span title={item.label} className="truncate">
                    {item.label}
                  </span>
                </AriaLink>
              )}
              {!isLast && (
                <icons.caretRight
                  aria-hidden
                  size={12}
                  className="shrink-0 text-[var(--color-text-disabled)] rtl:-scale-x-100"
                />
              )}
            </AriaBreadcrumb>
          );
        })}
      </AriaBreadcrumbs>
    </nav>
  );
}
